import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './CaseStudies.css';

const cases = [
  {
    id: 'ai-cooking',
    image: 'https://images.pexels.com/photos/9028916/pexels-photo-9028916.jpeg',
    tag: 'AI / Consumer Electronics',
    title: 'Smart AI Cooking Assistant Localization',
    client: 'European Consumer Electronics Brand',
    stat1: { n: '28%', l: 'BOM Cost Reduction' },
    stat2: { n: '3×', l: 'Faster NPI Cycle' },
    challenge: 'A European consumer electronics brand needed to localize their smart AI cooking assistant for the Indian market — redesigning power electronics, swapping components for locally sourced alternatives, and achieving BIS certification within 4 months.',
    solution: 'Zxeon FS handled complete design adaptation: re-layout of the power supply board for Indian voltage standards, component substitution with qualified local alternatives, and coordinated BIS testing. Our DFM analysis reduced the PCB layer count from 6 to 4, cutting BOM cost significantly.',
    result: 'Delivered production-ready boards in 14 weeks. BOM cost reduced by 28% vs. the original design. BIS certification achieved in first submission. Product launched on schedule.',
    tags: ['Consumer Electronics', 'NPI', 'Localization', 'Cost Optimization'],
  },
  {
    id: 'ev-battery',
    image: 'https://images.pexels.com/photos/110844/pexels-photo-110844.jpeg?auto=compress&cs=tinysrgb&w=800',
    tag: 'EV / Energy',
    title: 'EV Battery Management System Scaling',
    client: 'Indian EV Startup',
    stat1: { n: '1M+', l: 'Units/Year Capacity' },
    stat2: { n: '9mo', l: 'Prototype to Mass Production' },
    challenge: 'An Indian EV startup needed to scale their BMS board from 5,000 prototype units to 1M+ annual production in under 12 months, while meeting AIS-048 certification requirements and keeping per-unit cost below a tight target.',
    solution: 'We implemented a phased production ramp: NPI → pilot (50K) → mass (1M+). Design optimization removed two PCB layers, SMT automation replaced manual assembly, and we negotiated long-term supply agreements for key ICs, reducing component lead times from 16 weeks to 4.',
    result: 'Mass production commenced in Month 9. Per-unit cost reduced by 34%. Zero field failures in the first 200,000 units. Client scaled to second product line at Zxeon FS within 6 months.',
    tags: ['EV', 'Battery Management', 'Mass Production', 'Scale-up'],
  },
  {
    id: 'solar',
    image: 'https://images.pexels.com/photos/9875441/pexels-photo-9875441.jpeg?auto=compress&cs=tinysrgb&w=800',
    tag: 'Solar / Clean-tech',
    title: 'MPPT Controller for 50MW Solar Farm',
    client: 'Renewable Energy Developer',
    stat1: { n: '40%', l: 'BOM Cost Reduction' },
    stat2: { n: '99.2%', l: 'Field Reliability' },
    challenge: 'A renewable energy developer required 15,000 MPPT solar charge controller boards for a 50MW solar park. The existing design had high BOM cost and a field failure rate that exceeded acceptable warranty limits.',
    solution: 'Our engineers reverse-engineered the design intent, identified overspecified components, and proposed a redesigned topology that reduced component count by 18%. We introduced conformal coating for outdoor deployment and implemented rigorous burn-in testing for every unit.',
    result: 'BOM cost reduced by 40% vs. the original design. Field reliability improved to 99.2% over 18 months of operation. Client awarded us a follow-on contract for Phase 2 (25,000 units).',
    tags: ['Solar', 'Energy', 'Reliability', 'High Volume'],
  },
  {
    id: 'cleantech',
    image: 'https://images.pexels.com/photos/16647824/pexels-photo-16647824.jpeg',
    tag: 'Industrial IoT',
    title: 'Industrial IoT Sensor Production Scaling',
    client: 'Clean-tech Automation Company',
    stat1: { n: '98.7%', l: 'First-Pass Yield' },
    stat2: { n: '0', l: 'Field Returns in 12mo' },
    challenge: 'A clean-tech automation company was struggling with inconsistent quality in their industrial IoT sensor boards — first-pass yield below 85%, driving rework costs and delaying shipments. They needed a manufacturing partner who could solve the yield problem at scale.',
    solution: 'Zxeon FS performed a manufacturing process audit, identified solder paste deposition as the primary defect driver, and implemented SPC on all SMT lines. A dedicated AOI profile was created for the sensor board, and ICT fixtures were redesigned for 100% node coverage.',
    result: 'First-pass yield improved from 84% to 98.7% within 3 production runs. Rework cost eliminated. Zero field returns in the first 12 months of production. Client consolidated all manufacturing to Zxeon FS.',
    tags: ['Industrial IoT', 'Quality', 'SPC', 'Yield Improvement'],
  },
];

export default function CaseStudies() {
  const [open, setOpen] = useState(null);

  return (
    <main className="case-studies-page page-wrapper">
      <section className="page-hero">
        <div className="page-hero__bg" />
        <div className="container page-hero__inner">
          <div className="section-tag" style={{ background: 'rgba(0,194,224,0.15)', color: 'var(--cyan)' }}>Case Studies</div>
          <h1 className="page-hero__title">Real Projects. <span>Proven Results.</span></h1>
          <p className="page-hero__subtitle">
            A selection of engineering and manufacturing challenges we've solved for our clients.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="cases-list">
            {cases.map((c, i) => (
              <div key={c.id} className={`case-detail-card${open === c.id ? ' case-detail-card--open' : ''}`}>
                <div className="case-detail-header" onClick={() => setOpen(open === c.id ? null : c.id)}>
                  <div className="case-header-img-wrap">
                    <img src={c.image} alt={c.title} className="case-header-img" loading="lazy" />
                    <div className="case-header-img-overlay" />
                    <span className="case-tag-badge case-tag-badge--img">{c.tag}</span>
                  </div>
                  <div className="case-header-left">
                    <h2 className="case-detail-title">{c.title}</h2>
                    <span className="case-client">Client: {c.client}</span>
                  </div>
                  <div className="case-header-stats">
                    <div className="case-mini-stat">
                      <strong>{c.stat1.n}</strong>
                      <span>{c.stat1.l}</span>
                    </div>
                    <div className="case-mini-stat">
                      <strong>{c.stat2.n}</strong>
                      <span>{c.stat2.l}</span>
                    </div>
                    <div className="case-toggle">{open === c.id ? '−' : '+'}</div>
                  </div>
                </div>

                {open === c.id && (
                  <div className="case-detail-body">
                    <div className="case-body-grid">
                      <div className="case-section">
                        <div className="case-section-label">The Challenge</div>
                        <p>{c.challenge}</p>
                      </div>
                      <div className="case-section">
                        <div className="case-section-label">Our Solution</div>
                        <p>{c.solution}</p>
                      </div>
                      <div className="case-section">
                        <div className="case-section-label">The Result</div>
                        <p>{c.result}</p>
                      </div>
                    </div>
                    <div className="case-tags-row">
                      {c.tags.map(t => (
                        <span key={t} className="case-tag-pill">{t}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="section testimonials-section">
        <div className="container">
          <div className="section-center">
            <div className="section-tag">Client Testimonials</div>
            <h2 className="section-title">What Our <span>Clients Say</span></h2>
          </div>
          <div className="testimonials-grid">
            {[
              { quote: "Zxeon FS reduced our time-to-market by months. Their team understood our BOM at a level no other EMS partner had — they proactively flagged issues before they became problems.",  },
              { quote: "The quality improvement was dramatic. Moving manufacturing to Zxeon FS was the single best operational decision we made that year. Yield issues disappeared almost immediately." },
              { quote: "They're not just a vendor. When we had a critical component shortage three weeks before our launch deadline, their sourcing team found an alternative and qualified it in five days."},
            ].map((t) => (
              <div key={t.name} className="testimonial-card">
                <div className="testimonial-quote">"</div>
                <p>{t.quote}</p>
                <div className="testimonial-author">
                  <div className="testimonial-avatar">{t.init}</div>
                  <div>
                    <strong>{t.name}</strong>
                    <span>{t.role}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="cta-banner">
        <div className="container cta-banner__inner">
          <div className="cta-banner__text">
            <h2>Your Success Story <span>Starts Here</span></h2>
            <p>Let's talk about your product and how we can help you build it better, faster, and smarter.</p>
          </div>
          <div className="cta-banner__actions">
            <Link to="/contact" className="btn-primary">Start a Conversation →</Link>
            <Link to="/services" className="btn-outline-white">Our Services</Link>
          </div>
        </div>
      </section>
    </main>
  );
}
